import { createContext, ReactNode, useCallback, useState } from 'react';
import { StateSetter } from 'types/utils/React';
import { emptyFunction } from 'utils/functions';
import { SharedDocument } from 'types/Document';

export enum SlideDirectionType {
  LEFT = 'left',
  RIGHT = 'right',
}

export type ImageViewerContextProps = {
  currentViewedImage?: SharedDocument;
  imageDocuments: SharedDocument[];
  slideDirection: SlideDirectionType;
  handleCurrentViewedImage: (
    viewedImage: SharedDocument | undefined,
  ) => void;
};

const initialState = {
  currentViewedImage: undefined,
  imageDocuments: [],
  slideDirection: SlideDirectionType.LEFT,
  handleCurrentViewedImage: emptyFunction,
};

const imageViewerContext = createContext<ImageViewerContextProps>(initialState),
  { Provider } = imageViewerContext;

type ImageViewerContextProviderProps = {
  children: ReactNode;
  currentViewedImage?: SharedDocument;
  imageDocuments: SharedDocument[];
  setCurrentViewedImage: StateSetter<SharedDocument | undefined>;
};

const ImageViewerContextProvider = ({
  children,
  currentViewedImage,
  imageDocuments,
  setCurrentViewedImage,
}: ImageViewerContextProviderProps): JSX.Element => {
  const [slideDirection, setSlideDirection] = useState<SlideDirectionType>(
    SlideDirectionType.LEFT,
  );

  const handleCurrentViewedImage = useCallback(
    (viewedImage: SharedDocument | undefined) => {
      if (currentViewedImage && viewedImage) {
        const currentViewedImageIndex = imageDocuments.findIndex(
          (_) => _.id === currentViewedImage.id,
        );
        const viewedImageIndex = imageDocuments.findIndex(
          (_) => _.id === viewedImage.id,
        );
        const direction: SlideDirectionType =
          currentViewedImageIndex > viewedImageIndex
            ? SlideDirectionType.RIGHT
            : SlideDirectionType.LEFT;

        setSlideDirection(direction);
      }

      setCurrentViewedImage(viewedImage);
    },
    [
      currentViewedImage,
      imageDocuments,
      setCurrentViewedImage,
      setSlideDirection,
    ],
  );

  return (
    <Provider
      value={{
        currentViewedImage,
        imageDocuments,
        slideDirection,
        handleCurrentViewedImage,
      }}
    >
      {children}
    </Provider>
  );
};

export { imageViewerContext, ImageViewerContextProvider };
